import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../lib/api";
import { useTenant } from "../lib/tenant";
import Hint from "../components/Hint";
import Markdown from "../lib/markdown";

type Job = {
  job_id: string; agent: string; status: "queued" | "running" | "succeeded" | "failed";
  error?: string | null; result?: Record<string, any> | null; created_at?: string;
};
type BriefingItem = {
  id: string; title?: string; markdown?: string; model_route?: string; degraded?: boolean;
  created_at?: string; artifact_url?: string | null; kpi_count?: number; measured_count?: number;
  audience?: string; type?: string;
};
type ExportItem = {
  id: string; format?: string; status?: string; artifact_filename?: string | null;
  artifact_bytes?: number | null; artifact_note?: string | null; created_at?: string;
};
type AgentInfo = { agent: string; title: string; state: "live" | "partial" | "inactive"; detail: string };
type AgentsStatus = {
  worker: { worker_running: boolean; ready: boolean; jobs?: Record<string, number> };
  llm: { configured: boolean; model: string; policy: string };
  storage: { configured: boolean; reachable?: boolean; bucket: string };
  agents: AgentInfo[];
};

const STATE_LABEL: Record<AgentInfo["state"], string> = {
  live: "aktiv",
  partial: "teilweise",
  inactive: "nicht aktiv",
};

export default function BriefingAgentPage() {
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  const qc = useQueryClient();
  const [jobId, setJobId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [audience, setAudience] = useState("cfo");
  const [type, setType] = useState("weekly");

  const status = useQuery({
    queryKey: ["agents-status", effectiveTenantId],
    queryFn: () => api.get<AgentsStatus>("/agents/status"),
    refetchInterval: 20_000,
  });
  const briefings = useQuery({
    queryKey: ["agent-briefings", effectiveTenantId],
    queryFn: () => api.get<BriefingItem[]>("/agents/briefings?limit=5"),
  });
  const exports = useQuery({
    queryKey: ["agent-exports", effectiveTenantId],
    queryFn: () => api.get<ExportItem[]>("/agents/exports?limit=8"),
  });
  const job = useQuery({
    queryKey: ["agent-job", jobId],
    queryFn: () => api.get<Job>(`/agents/jobs/${jobId}`),
    enabled: Boolean(jobId),
    refetchInterval: (q) => {
      const s = (q.state.data as Job | undefined)?.status;
      return s === "succeeded" || s === "failed" ? false : 2500;
    },
  });

  useEffect(() => {
    const s = job.data?.status;
    if (s === "succeeded") {
      qc.invalidateQueries({ queryKey: ["agent-briefings", effectiveTenantId] });
      qc.invalidateQueries({ queryKey: ["agent-exports", effectiveTenantId] });
      setBusy(false);
    }
    if (s === "failed") setBusy(false);
  }, [job.data?.status, qc, effectiveTenantId]);

  const start = async () => {
    setErr(null); setBusy(true);
    try {
      const r = await api.post<{ job_id: string }>("/agents/briefing", { audience, type });
      setJobId(r.job_id);
    } catch (e: any) {
      setBusy(false);
      setErr(e?.details?.detail ?? e?.message ?? "Start fehlgeschlagen");
    }
  };

  const startExport = async (format: string) => {
    setErr(null); setBusy(true);
    try {
      const r = await api.post<{ job_id: string }>("/agents/export", { format, audience, type: "executive_summary" });
      setJobId(r.job_id);
    } catch (e: any) {
      setBusy(false);
      setErr(e?.details?.detail ?? e?.message ?? "Export fehlgeschlagen");
    }
  };

  const download = async (id: string) => {
    try {
      const r = await api.get<{ url: string }>(`/agents/exports/${id}/download`);
      window.open(r.url, "_blank", "noopener");
    } catch (e: any) {
      setErr(e?.details?.detail ?? "Download-Link konnte nicht erzeugt werden.");
    }
  };

  const st = status.data;
  const running = job.data?.status === "queued" || job.data?.status === "running";
  const latest = briefings.data?.[0];

  return (
    <section className="help-page">
      <header className="help-hero">
        <p className="hero-eyebrow">Agenten</p>
        <h1>Management-Briefing<Hint term="briefing" /></h1>
        <p className="hero-sub">
          Ein echter Agent liest Ihre Kennzahlen, den Treiberbaum, die Quellen und den
          Arbeitspaket-Status und schreibt daraus ein Briefing — mit einem <strong>lokalen</strong>{" "}
          Sprachmodell auf der eigenen Hardware. Ihre Daten verlassen die Plattform nicht.
        </p>
      </header>

      {st && (
        <section className="help-block">
          <h2>Was gerade wirklich läuft</h2>
          <div className="agent-facts">
            <div>
              <span>Worker</span>
              <strong className={st.worker.worker_running ? "ok" : "bad"}>
                {st.worker.worker_running ? "läuft" : "gestoppt"}
              </strong>
              <p>{Object.entries(st.worker.jobs ?? {}).map(([k, v]) => `${v}× ${k}`).join(" · ") || "noch keine Aufträge"}</p>
            </div>
            <div>
              <span>Sprachmodell</span>
              <strong className={st.llm.configured ? "ok" : "bad"}>{st.llm.model}</strong>
              <p>{st.llm.policy}</p>
            </div>
            <div>
              <span>Objektspeicher</span>
              <strong className={st.storage.reachable ? "ok" : "bad"}>
                {st.storage.reachable ? "erreichbar" : "nicht erreichbar"}
              </strong>
              <p>Bucket {st.storage.bucket}</p>
            </div>
          </div>
          <ul className="help-features">
            {st.agents.map((a) => (
              <li key={a.agent} className={`help-feature stand-${a.state === "live" ? "live" : a.state === "partial" ? "teilweise" : "geplant"}`}>
                <span className="help-badge">{STATE_LABEL[a.state]}</span>
                <div><strong>{a.title}</strong><p>{a.detail}</p></div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="help-block">
        <h2>Briefing erzeugen</h2>
        <div className="agent-controls">
          <label>
            <span>Zielgruppe</span>
            <select value={audience} onChange={(e) => setAudience(e.target.value)}>
              <option value="cfo">CFO</option>
              <option value="ceo">CEO</option>
              <option value="coo">COO</option>
              <option value="controller">Controlling</option>
            </select>
          </label>
          <label>
            <span>Zeitraum</span>
            <select value={type} onChange={(e) => setType(e.target.value)}>
              <option value="weekly">wöchentlich</option>
              <option value="monthly">monatlich</option>
              <option value="ad_hoc">ad hoc</option>
            </select>
          </label>
          <button className="btn primary" onClick={start} disabled={busy || running}>
            {running ? "Agent arbeitet …" : "Briefing erzeugen"}
          </button>
        </div>

        {running && (
          <div className="agent-progress">
            <div className="agent-progress-bar"><span /></div>
            <p className="muted small">
              Der Agent sammelt die Fakten und lässt das lokale Modell schreiben. Das dauert
              typischerweise ein bis zwei Minuten — das Fenster darf geschlossen werden, der Auftrag
              läuft im Hintergrund weiter.
            </p>
          </div>
        )}
        {job.data?.status === "failed" && (
          <p className="error">Der Agent ist gescheitert: {job.data.error}</p>
        )}
        {err && <p className="error">{err}</p>}
      </section>

      {latest?.markdown ? (
        <section className="help-block briefing-view">
          <div className="section-head">
            <div>
              <h2>{latest.title ?? "Briefing"}</h2>
              <p className="muted small">
                Modell {latest.model_route} · {latest.measured_count}/{latest.kpi_count} Kennzahlen mit
                echtem Messwert
                {latest.degraded ? " · Achtung: ohne Sprachmodell erzeugt (nur Faktenteil)" : ""}
              </p>
            </div>
            <div className="briefing-actions">
              <button className="btn ghost small" onClick={() => startExport("md")} disabled={busy}>Als Markdown</button>
              <button className="btn ghost small" onClick={() => startExport("xlsx")} disabled={busy}>Als Excel</button>
              <button className="btn ghost small" onClick={() => startExport("csv")} disabled={busy}>Als CSV</button>
            </div>
          </div>
          <Markdown text={latest.markdown} />
        </section>
      ) : (
        <section className="help-block">
          <div className="empty-friendly">
            <strong>Noch kein Briefing vorhanden.</strong>
            <p className="muted">
              Klicken Sie oben auf „Briefing erzeugen". Der Agent liest ausschließlich Ihre eigenen
              Daten — er erfindet nichts. Fehlt für eine Kennzahl ein Messwert, sagt er das ausdrücklich.
            </p>
          </div>
        </section>
      )}

      <section className="help-block">
        <h2>Exportierte Dateien</h2>
        {(exports.data?.length ?? 0) === 0 && (
          <p className="muted small">Noch keine Exporte. Oben erzeugen — die Datei landet im internen
            Objektspeicher und der Download-Link ist sieben Tage gültig.</p>
        )}
        <ul className="export-list">
          {(exports.data ?? []).map((e) => (
            <li key={e.id} className="export-row">
              <div>
                <strong>{e.artifact_filename ?? e.format?.toUpperCase() ?? "Export"}</strong>
                <p className="muted small">
                  {e.created_at ? new Date(e.created_at).toLocaleString("de-DE") : ""}
                  {e.artifact_bytes ? ` · ${(e.artifact_bytes / 1024).toFixed(1)} kB` : ""}
                  {e.artifact_note ? ` · ${e.artifact_note}` : ""}
                </p>
              </div>
              <span className={`status-pill ${e.status === "succeeded" ? "good" : "warning"}`}>{e.status}</span>
              <button
                className="btn small"
                disabled={e.status !== "succeeded"}
                onClick={() => download(e.id)}
              >
                Herunterladen
              </button>
            </li>
          ))}
        </ul>
      </section>
    </section>
  );
}
